import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = {
  error: Error | null;
  chunkError: boolean;
  componentStack: string | null;
};

/**
 * Error boundary that wraps the lazy-loaded admin tree. Two failure modes
 * we explicitly handle:
 *
 *   1. Chunk-load failure (stale cached index.html points at a JS file the
 *      latest deploy doesn't have anymore). We force a hard reload so the
 *      browser pulls a fresh index.html with the right asset hashes.
 *
 *   2. Anything else thrown during render — we render a visible error
 *      message instead of the previous silent-collapse-to-body-bg
 *      ("black screen on mobile") behavior.
 */
export class AdminErrorBoundary extends Component<Props, State> {
  state: State = { error: null, chunkError: false, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Vite/Rollup chunk-load failures throw with one of these shapes
    // depending on browser; hash-mismatch-after-deploy lands here.
    const msg = String(error?.message ?? "");
    const isChunkError =
      /Loading chunk \d+ failed/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /ChunkLoadError/i.test(error?.name ?? "") ||
      /import.*failed/i.test(msg);
    return { error, chunkError: isChunkError };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("AdminErrorBoundary caught:", error, info);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  componentDidUpdate(_: Props, prev: State): void {
    if (this.state.chunkError && !prev.chunkError) {
      // Force a hard refresh — strip query/hash and reload index.html so the
      // SPA picks up the latest asset URLs. Tiny delay so the user sees
      // there's a hand-off happening rather than the screen flickering.
      setTimeout(() => {
        // Skip the cache where possible.
        window.location.replace(window.location.pathname);
      }, 800);
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    if (this.state.chunkError) {
      return (
        <div className="min-h-screen grid place-items-center bg-void text-bone px-4 text-center">
          <div className="space-y-3">
            <div className="font-display text-base text-mystic uppercase tracking-widest">
              Updating admin…
            </div>
            <div className="font-terminal text-sm text-aura/85 max-w-xs mx-auto">
              A new version was deployed. Loading fresh assets.
            </div>
          </div>
        </div>
      );
    }

    const fullDump = [
      `MESSAGE: ${this.state.error.message || String(this.state.error)}`,
      this.state.error.stack ? `\nSTACK:\n${this.state.error.stack}` : "",
      this.state.componentStack
        ? `\nCOMPONENT STACK:${this.state.componentStack}`
        : "",
    ]
      .filter(Boolean)
      .join("\n");

    return (
      <div className="min-h-screen bg-void text-bone p-4 md:p-8 flex flex-col items-center justify-center">
        <div className="max-w-2xl w-full bevel-out bg-grimoire p-1">
          <div className="bg-night p-5 border-2 border-black space-y-4">
            <div className="font-display text-base text-ember uppercase tracking-widest">
              ⚠ Admin crashed
            </div>
            <p className="font-pixel text-sm text-aura/85">
              Something threw during render. The full error + stack + React
              component stack are below — copy them and paste in chat so
              the failing component can be pinpointed.
            </p>
            <pre className="bevel-in bg-void p-3 font-terminal text-[11px] text-ember whitespace-pre-wrap break-words max-h-80 overflow-auto">
              {fullDump}
            </pre>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(fullDump);
                  } catch {
                    /* ignore */
                  }
                }}
                className="font-display text-[10px] uppercase tracking-widest px-3 py-2 border-2 border-mystic text-mystic hover:text-bone hover:bg-witch/30 transition-colors pointer"
              >
                Copy
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="font-display text-[10px] uppercase tracking-widest px-3 py-2 bg-acid text-void border-2 border-black shadow-[3px_3px_0_0_#000] hover:bg-bone transition-all pointer"
              >
                Reload
              </button>
              <a
                href="/admin/login"
                className="font-display text-[10px] uppercase tracking-widest px-3 py-2 border-2 border-mystic text-mystic hover:text-bone hover:bg-witch/30 transition-colors pointer"
              >
                Sign in again
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
